import { type SEOHandle } from '@nasa-gcn/remix-seo'
import { type User } from '@prisma/client'
import {
	json,
	type LoaderFunctionArgs,
	type MetaFunction,
} from '@remix-run/node'
import { Link, redirect, useFetcher, useLoaderData } from '@remix-run/react'
import { InfoIcon } from 'lucide-react'
import posthog from 'posthog-js'
import { useEffect, useState } from 'react'
import { Button } from '#app/components/ui/button'
import { Input } from '#app/components/ui/input'
import { Label } from '#app/components/ui/label'
import { Textarea } from '#app/components/ui/textarea'

import { getUserById } from '#app/models/user/get-user'
import { Interval, PlanId } from '#app/services/stripe/plans'
import { useStoreContext } from '#app/store'
import { authenticator } from '#app/utils/auth.server'
import { isEarthDay } from '#app/utils/is-earth-day.js'

type LoaderData = {
	user: User | null
}

export const handle: SEOHandle = {
	getSitemapEntries: () => null,
}

export async function loader({ request }: LoaderFunctionArgs) {
	const session = await authenticator.isAuthenticated(request)

	if (!session) return json<LoaderData>({ user: null })

	const user = await getUserById(session.id)

	if (user && !user.name) {
		return redirect('/profile/edit?onboarding=true&redirectTo=/gift-basket')
	}

	return json<LoaderData>({ user })
}

export const meta: MetaFunction<typeof loader> = ({ data }) => [
	{ title: `Your gift basket | Erda` },
	{
		name: 'description',
		content: `Your selection of Places to gift. Each gifted Place helps plant two trees in developing communities, combating poverty and environmental harm.`,
	},
	{
		name: 'keywords',
		content: 'gift basket',
	},
	{ name: 'og:title', content: `Your gift basket | Erda` },
	{ name: 'og:type', content: 'website' },
	{ name: 'og:url', content: 'https://erda.app' },
	{
		name: 'og:image',
		content: `https://erda.app/image/gift-basket.png`,
	},
	{ name: 'og:card', content: 'summary_large_image' },
	{ name: 'og:creator', content: '@erda_app' },
	{ name: 'og:site', content: 'https://erda.app' },
	{
		name: 'og:description',
		content: `Your selection of Places to gift. Each gifted Place helps plant two trees in developing communities, combating poverty and environmental harm.`,
	},
	{
		name: 'twitter:image',
		content: `https://erda.app/image/gift-basket.png`,
	},
	{ name: 'twitter:card', content: 'summary_large_image' },
	{ name: 'twitter:creator', content: '@erda_app' },
	{ name: 'twitter:title', content: `Your gift basket | Erda` },
	{
		name: 'twitter:description',
		content: `Your selection of Places to gift. Each gifted Place helps plant two trees in developing communities, combating poverty and environmental harm.`,
	},
]

export default function Basket() {
	const loaderData = useLoaderData() as LoaderData

	const selectedPlotIds = useStoreContext(s => s.selectedPlotIds)

	const fetcher = useFetcher<{ error?: string }>()
	const isLoading = fetcher.state !== 'idle'

	const planId = PlanId.PLOT_GIFT
	const planInterval = Interval.YEAR

	const costPerPlot = 2
	const treesPerPlot = 2

	const totalCost = (selectedPlotIds.length * costPerPlot).toFixed(2)
	const plotText = `${selectedPlotIds.length > 1 ? 'these places' : 'this place'}`

	const [recipientEmail, setRecipientEmail] = useState('')
	const [recipientName, setRecipientName] = useState('')
	const [message, setMessage] = useState('')

	const [draftLoaded, setDraftLoaded] = useState(false)
	useEffect(() => {
		try {
			const saved = JSON.parse(
				sessionStorage.getItem('erda:gift-draft') || 'null',
			)
			if (saved && typeof saved === 'object') {
				if ('email' in saved && typeof saved.email === 'string')
					setRecipientEmail(saved.email.slice(0, 254))
				if ('name' in saved && typeof saved.name === 'string')
					setRecipientName(saved.name.slice(0, 50))
				if ('message' in saved && typeof saved.message === 'string')
					setMessage(saved.message.slice(0, 160))
			}
		} catch {
			/* Storage can be unavailable in private browsing. */
		}
		setDraftLoaded(true)
	}, [])
	useEffect(() => {
		if (!draftLoaded) return
		try {
			sessionStorage.setItem(
				'erda:gift-draft',
				JSON.stringify({ email: recipientEmail, name: recipientName, message }),
			)
		} catch {
			/* Keep the form usable without storage. */
		}
	}, [draftLoaded, recipientEmail, recipientName, message])

	// Robust email validation supporting subaddressing (+) and other common characters
	const isValidEmail = recipientEmail
		.trim()
		.match(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/)

	if (selectedPlotIds.length === 0)
		return (
			<article className="panel-copy">
				<h1>Choose a place to give.</h1>
				<p>
					Pick somewhere meaningful on the map. Gifts cost $2 per place, per
					year.
				</p>
				<Button asChild>
					<Link to="/?map=1&select=paid">Find a place to gift</Link>
				</Button>
			</article>
		)
	return (
		<div className="flex min-h-full flex-col justify-between">
			<div className="flex flex-col gap-3 pb-4">
				{/* <h2 className="flex gap-1 font-bold">

					Gifting Plots
				</h2> */}
				<h1 className="sr-only">Gift basket</h1>
				{fetcher.data?.error && (
					<p role="alert" className="rounded-lg border p-4">
						{fetcher.data.error}
					</p>
				)}
				<p>Gift {plotText} to someone</p>

				<div className="flex w-full flex-1 flex-col justify-between gap-6 py-4">
					<div className="grid w-full items-center gap-1.5">
						<div className="flex justify-between">
							<Label htmlFor="email">Recipient's email</Label>
						</div>
						<Input
							data-vaul-no-drag
							placeholder="example@email.com"
							value={recipientEmail}
							type="email"
							id="email"
							onChange={e => setRecipientEmail(e.target.value)}
						/>
					</div>

					<div className="grid w-full items-center gap-1.5">
						<div className="flex justify-between">
							<Label htmlFor="name">Recipient's name</Label>
							<span className="text-xs text-muted-foreground">
								{recipientName.length} / 50
							</span>
						</div>
						<Input
							data-vaul-no-drag
							placeholder="Chris"
							maxLength={50}
							value={recipientName}
							type="text"
							id="name"
							onChange={e => setRecipientName(e.target.value)}
						/>
					</div>

					<div className="grid w-full items-center gap-1.5">
						<div className="flex justify-between">
							<Label htmlFor="message">Message</Label>
							<span className="text-xs text-muted-foreground">
								{message.length ?? 0} / 160
							</span>
						</div>
						<Textarea
							data-vaul-no-drag
							placeholder="Remember this spot?"
							maxLength={160}
							id="message"
							value={message}
							onChange={e => setMessage(e.target.value)}
						/>
					</div>
				</div>
				<p className="text-sm font-semibold"></p>

				<div className="relative flex gap-2 rounded-lg bg-muted p-3">
					<InfoIcon className="mt-0.5 h-4 w-4 flex-shrink-0" />
					<div className="space-y-2">
						<p className="text-sm">
							After checkout, customise {plotText} and choose when to send the
							gift email.
						</p>
					</div>
				</div>
			</div>

			<div className="flex w-full shrink-0 flex-col gap-3 border-t bg-background pb-2 pt-4">
				<p className="text-center text-base font-semibold">
					${totalCost} / year
				</p>
				<p className="text-center text-sm">
					You pay for the gift and its annual renewals. The recipient pays
					nothing.
				</p>
				<p className="text-center text-sm text-muted-foreground">
					Funds {selectedPlotIds.length * (isEarthDay() ? 3 : treesPerPlot)}{' '}
					trees now and {selectedPlotIds.length * treesPerPlot} on each renewal.
				</p>
				<Link
					to="/tree-planting"
					className="text-center text-xs underline underline-offset-4"
				>
					How tree funding works
				</Link>

				{loaderData.user ? (
					// TODO (med) check if the plots are still available (somone else may have bought them in the meantime)
					<fetcher.Form
						action="/resources/stripe/create-checkout"
						method="post"
						className="w-full"
					>
						<div className="flex flex-col gap-2">
							<Button
								data-vaul-no-drag
								name="plan"
								value={JSON.stringify({
									planId,
									planInterval,
									selectedPlots: selectedPlotIds,
									giftMessage: message,
									giftRecipientEmail: recipientEmail.trim().toLowerCase(),
									giftRecipientName: recipientName.trim(),
								})}
								type="submit"
								disabled={!isValidEmail || !recipientName.trim() || isLoading}
								className="relative w-full"
								onClick={() => {
									posthog.capture('gift_checkout_initiated', {
										selectedPlots: selectedPlotIds,
									})
								}}
							>
								<span>{isLoading ? 'Loading...' : `Gift ${plotText}`}</span>
							</Button>
						</div>
						<div className="mt-2 flex justify-center">
							<p className="text-xs text-muted-foreground">
								By continuing, you agree to our{' '}
								<Button variant="link" asChild className="text-xs">
									<Link to="/terms?from=/gift-basket">Terms</Link>
								</Button>{' '}
								and{' '}
								<Button variant="link" asChild className="text-xs">
									<Link to="/privacy?from=/gift-basket">Privacy Policy</Link>
								</Button>
								.
							</p>
						</div>
					</fetcher.Form>
				) : (
					<div className="flex flex-col gap-2">
						<Button
							asChild
							data-vaul-no-drag
							className="w-full"
							onClick={() => {
								posthog.capture('gift_redirect_login', {
									selectedPlots: selectedPlotIds,
								})
							}}
						>
							<Link to="/login?redirectTo=/gift-basket">
								<span>{`Gift ${plotText}`}</span>
							</Link>
						</Button>
					</div>
				)}
			</div>
		</div>
	)
}
